import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ServicoTipoService } from '@/core/api/sicom-api/servicos/shared/servico-tipo.service';
import type { ServicoTipoResponse } from '@/core/api/sicom-api/servicos/shared/servico-tipo.model';
import { type TipoServicoPessoa, obterServicoDisponivelPorId } from '@/features/servicos/shared/enums/servico-disponivel.enum';
import { LoggerService } from '@/core/services/logger.service';

/** Um serviço do módulo: o rótulo e o setor vêm do banco, o `codigo` (rota e registry) vem do código. */
export interface ServicoDoModulo {
    id: number;
    codigo: string;
    rotulo: string;
}

/**
 * O catálogo do módulo, casando o que o servidor sabe (`id` → rótulo, e a que setor pertence) com o
 * que só o código sabe (`id` → `codigo` de rota).
 *
 * <p>`carregarPara` é chamado APENAS pelo `ServicosContent`, o componente dono do `:tipo` — um
 * escritor, vários leitores. Menu, Consultar Protocolo e título do dashboard só leem.</p>
 */
@Injectable({ providedIn: 'root' })
export class CatalogoServicosStore {
    private readonly servicoTipoService = inject(ServicoTipoService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly logger = inject(LoggerService);

    private readonly _servicos = signal<ServicoDoModulo[]>([]);
    private readonly _carregando = signal(false);
    private readonly _erro = signal<string | null>(null);

    /** Descarta resposta de setor que o analista já deixou — trocar de módulo rápido inverteria a ordem. */
    private requisicaoAtual = 0;

    readonly servicos = this._servicos.asReadonly();
    readonly carregando = this._carregando.asReadonly();
    readonly erro = this._erro.asReadonly();

    private readonly rotuloPorCodigo = computed(() => new Map(this._servicos().map((servico) => [servico.codigo, servico.rotulo])));

    carregarPara(tipo: TipoServicoPessoa): void {
        const requisicao = ++this.requisicaoAtual;
        this._carregando.set(true);
        this._erro.set(null);
        this._servicos.set([]);

        this.servicoTipoService
            .listarPorSetor(tipo === 'pessoa-juridica' ? 'PESSOA_JURIDICA' : 'PESSOA_FISICA')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (linhas) => {
                    if (requisicao !== this.requisicaoAtual) {
                        return;
                    }
                    this._servicos.set(this.casarComCodigoLocal(linhas ?? []));
                    this._carregando.set(false);
                },
                error: (erro) => {
                    if (requisicao !== this.requisicaoAtual) {
                        return;
                    }
                    // Ponto único de falha da navegação do módulo (sem catálogo não há menu nem
                    // dropdown), e este módulo não tem `<p-toast>` — o toast do interceptor global
                    // também não desenha. Sem registrar aqui, um 403/500 real vira só a frase genérica
                    // abaixo, sem rastro nenhum de qual foi o erro de verdade.
                    this.logger.error('Falha ao carregar o catálogo de serviços:', erro);
                    this._servicos.set([]);
                    this._erro.set('Não foi possível carregar os serviços do módulo.');
                    this._carregando.set(false);
                }
            });
    }

    rotuloDoCodigo(codigo: string): string | undefined {
        return this.rotuloPorCodigo().get(codigo);
    }

    /**
     * Linha do servidor sem `codigo` local fica fora: o menu não teria para onde apontar e o dashboard
     * não teria componente para abrir. A guarda contra isso é de build, não de tela — ver o teste do
     * `V7` que exige setor para os 27 do catálogo, e o spec que exige os mesmos 27 no mapa local.
     */
    private casarComCodigoLocal(linhas: ServicoTipoResponse[]): ServicoDoModulo[] {
        return linhas
            .map((linha) => ({ linha, codigo: obterServicoDisponivelPorId(linha.id)?.codigo }))
            .filter((par): par is { linha: ServicoTipoResponse; codigo: string } => par.codigo !== undefined)
            .map(({ linha, codigo }) => ({ id: linha.id, codigo, rotulo: linha.rotulo }));
    }
}
